import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Loader2, ArrowDownLeft, ArrowUpRight, TrendingUp, TrendingDown,
  FileSpreadsheet, RotateCcw, Scale, Calendar
} from 'lucide-react';
import { formatDate } from '../lib/formatUtils';
import { exportToExcel } from '../lib/exportUtils';
import TruncatedText from '../components/common/TruncatedText';

const formatCurrency = (a) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(a || 0);

// Local-date helper (NOT toISOString — that converts to UTC and breaks past-
// midnight east-of-UTC users).
const _isoLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const todayISO = () => _isoLocal(new Date());
const startOfMonthISO = () => {
  const d = new Date();
  d.setDate(1);
  return _isoLocal(d);
};

const PRESETS = [
  { key: 'this-month', label: 'This Month' },
  { key: 'last-month', label: 'Last Month' },
  { key: 'this-fy', label: 'This FY' },
  { key: 'last-30', label: 'Last 30 Days' },
  { key: 'last-90', label: 'Last 90 Days' },
];

const getPresetRange = (key) => {
  const today = new Date();
  const iso = _isoLocal;
  if (key === 'this-month') {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: iso(from), to: iso(today) };
  }
  if (key === 'last-month') {
    const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const to = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: iso(from), to: iso(to) };
  }
  if (key === 'this-fy') {
    // Indian FY: Apr 1 → Mar 31
    const fyStart = today.getMonth() >= 3
      ? new Date(today.getFullYear(), 3, 1)
      : new Date(today.getFullYear() - 1, 3, 1);
    return { from: iso(fyStart), to: iso(today) };
  }
  if (key === 'last-30') {
    const from = new Date(today);
    from.setDate(from.getDate() - 30);
    return { from: iso(from), to: iso(today) };
  }
  if (key === 'last-90') {
    const from = new Date(today);
    from.setDate(from.getDate() - 90);
    return { from: iso(from), to: iso(today) };
  }
  return { from: iso(today), to: iso(today) };
};

export const StatementPage = () => {
  const [dateFrom, setDateFrom] = useState(startOfMonthISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({ total_credit: 0, total_debit: 0, credit_count: 0, debit_count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, [dateFrom, dateTo]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Statement legitimately needs every row in the date range to render
      // the full Income / Expense lists, not just a single page. The new
      // `?all=true` flag on /api/transactions bypasses pagination while
      // still honoring all the other filters.
      const params = new URLSearchParams({ all: 'true' });
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const res = await api.get(`/transactions?${params.toString()}`);
      if (res.data.success) {
        setTransactions(res.data.data || []);
        setSummary(res.data.summary || { total_credit: 0, total_debit: 0, credit_count: 0, debit_count: 0 });
      }
    } catch {
      setTransactions([]);
      setSummary({ total_credit: 0, total_debit: 0, credit_count: 0, debit_count: 0 });
    } finally { setLoading(false); }
  };

  const applyPreset = (key) => {
    const { from, to } = getPresetRange(key);
    setDateFrom(from);
    setDateTo(to);
  };

  const credits = useMemo(() => transactions.filter(t => t.type === 'credit'), [transactions]);
  const debits = useMemo(() => transactions.filter(t => t.type === 'debit'), [transactions]);

  const totalIncome = summary.total_credit || 0;
  const totalExpenses = summary.total_debit || 0;
  const net = totalIncome - totalExpenses;

  const handleExport = () => {
    const rows = [
      ...credits.map(t => ({ section: 'Income', date: t.date, description: t.description || t.client_name || '', category: t.category, amount: t.amount })),
      ...debits.map(t => ({ section: 'Expense', date: t.date, description: t.description || '', category: t.category, amount: t.amount })),
    ];
    exportToExcel(rows, [
      { key: 'section', header: 'Section' },
      { key: 'date', header: 'Date' },
      { key: 'description', header: 'Description' },
      { key: 'category', header: 'Category' },
      { key: 'amount', header: 'Amount' },
    ], `Statement_${dateFrom}_to_${dateTo}`);
  };

  const resetRange = () => {
    setDateFrom(startOfMonthISO());
    setDateTo(todayISO());
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="statement-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Statement</h1>
          <p className="text-muted-foreground">Income and expenses summary for any date range</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="statement-export-btn">
            <FileSpreadsheet className="h-4 w-4 mr-1" />Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs text-muted-foreground mb-1.5 block">From</label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="statement-date-from" />
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs text-muted-foreground mb-1.5 block">To</label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="statement-date-to" />
            </div>
            <Button variant="outline" size="sm" onClick={resetRange} data-testid="statement-reset-btn">
              <RotateCcw className="h-4 w-4 mr-1" />Reset
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1 mr-1"><Calendar className="h-3 w-3" />Quick range:</span>
            {PRESETS.map(p => (
              <button
                key={p.key}
                type="button"
                onClick={() => applyPreset(p.key)}
                className="text-xs px-2.5 py-1 rounded-full border border-border hover:border-primary/40 hover:bg-primary/5 transition-colors text-muted-foreground hover:text-foreground"
                data-testid={`statement-preset-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="stat-card border-emerald-500/20" data-testid="statement-total-income">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Total Income</p>
                <p className="text-2xl font-bold text-emerald-500 mt-1">{formatCurrency(totalIncome)}</p>
                <p className="text-xs text-muted-foreground mt-1">{summary.credit_count || 0} credit transaction{(summary.credit_count || 0) === 1 ? '' : 's'}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card border-red-500/20" data-testid="statement-total-expenses">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Total Expenses</p>
                <p className="text-2xl font-bold text-red-500 mt-1">{formatCurrency(totalExpenses)}</p>
                <p className="text-xs text-muted-foreground mt-1">{summary.debit_count || 0} debit transaction{(summary.debit_count || 0) === 1 ? '' : 's'}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <TrendingDown className="h-6 w-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={`stat-card ${net >= 0 ? 'border-primary/30' : 'border-amber-500/30'}`} data-testid="statement-net">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Net (Income − Expenses)</p>
                <p className={`text-2xl font-bold mt-1 ${net >= 0 ? 'text-primary' : 'text-amber-500'}`}>
                  {net >= 0 ? '' : '−'}{formatCurrency(Math.abs(net))}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {net >= 0 ? 'Surplus for the period' : 'Shortfall for the period'}
                </p>
              </div>
              <div className={`h-12 w-12 rounded-full ${net >= 0 ? 'bg-primary/10' : 'bg-amber-500/10'} flex items-center justify-center`}>
                <Scale className={`h-6 w-6 ${net >= 0 ? 'text-primary' : 'text-amber-500'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Income & Expenses side-by-side */}
      {loading ? (
        <Card><CardContent className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Income */}
          <Card data-testid="statement-income-card">
            <CardContent className="p-0">
              <div className="px-5 py-3 border-b border-border bg-emerald-500/5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full bg-emerald-500/10 flex items-center justify-center">
                    <ArrowDownLeft className="h-4 w-4 text-emerald-500" />
                  </div>
                  <span className="font-semibold">Income</span>
                  <Badge className="bg-emerald-500/10 text-emerald-500 ml-1">{credits.length}</Badge>
                </div>
                <span className="text-sm font-mono font-semibold text-emerald-500">{formatCurrency(totalIncome)}</span>
              </div>
              {credits.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                  <ArrowDownLeft className="h-8 w-8 mb-2 opacity-40" />
                  <p className="text-sm">No income in this range</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {credits.map(t => (
                      <TableRow key={t.id} data-testid={`statement-credit-row-${t.id}`}>
                        <TableCell className="text-sm whitespace-nowrap">{formatDate(t.date)}</TableCell>
                        <TableCell>
                          <TruncatedText
                            text={t.description || t.client_name || t.invoice_number || 'Credit'}
                            lines={2}
                            className="text-sm font-medium max-w-[260px]"
                          />
                          {t.invoice_number && <p className="text-xs text-muted-foreground truncate max-w-[260px]">{t.invoice_number}</p>}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold text-emerald-500">+{formatCurrency(t.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Expenses */}
          <Card data-testid="statement-expenses-card">
            <CardContent className="p-0">
              <div className="px-5 py-3 border-b border-border bg-red-500/5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full bg-red-500/10 flex items-center justify-center">
                    <ArrowUpRight className="h-4 w-4 text-red-500" />
                  </div>
                  <span className="font-semibold">Expenses</span>
                  <Badge className="bg-red-500/10 text-red-500 ml-1">{debits.length}</Badge>
                </div>
                <span className="text-sm font-mono font-semibold text-red-500">{formatCurrency(totalExpenses)}</span>
              </div>
              {debits.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                  <ArrowUpRight className="h-8 w-8 mb-2 opacity-40" />
                  <p className="text-sm">No expenses in this range</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {debits.map(t => (
                      <TableRow key={t.id} data-testid={`statement-debit-row-${t.id}`}>
                        <TableCell className="text-sm whitespace-nowrap">{formatDate(t.date)}</TableCell>
                        <TableCell>
                          <TruncatedText
                            text={t.description || 'Expense'}
                            lines={2}
                            className="text-sm font-medium max-w-[260px]"
                          />
                          {t.category && <p className="text-xs text-muted-foreground capitalize">{t.category.replace('_', ' ')}</p>}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold text-red-500">−{formatCurrency(t.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default StatementPage;
